import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import Select from '../../../../components/Select';
import { TextInput } from '../../../../components/Inputs/TextInput';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import { openModalAction } from '../../../../actions/modal';
import NewConfirmationModal from '../../../../modals/NewConfirmationModal/NewConfirmationModal';
import Strings from '../../../../Strings';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import { actions, LIST_ACCESS_OBLIGATIONS_RESULT } from './actions';
import useAccessOrgSelection from '../useAccessOrgSelection';
import styles from './AccessObligations.module.scss';

// The deadline cron is a GeneralWork job running every 5 minutes; after a move
// the tester has to wait for it before the status changes. The server words this
// (cron_note) so the wording can never drift from the real schedule.
const DEFAULT_CRON_NOTE = 'The deadline cron runs every 5 minutes.';

const fmtDate = value => (value ? moment(value).format(DATE_TIME_SPACE_12_UPPERCASE) : '-');

const fmtWindow = row => {
  if (!row.window_start && !row.window_end) {
    return '-';
  }
  return `${fmtDate(row.window_start)} - ${fmtDate(row.window_end)}`;
};

// clampDays keeps what the tester typed inside the range the server allows for
// this action; anything unparseable falls back to the action's default. Some
// actions (past_deadline, the enrollment-level ones) need the offset to be at
// least 1 - the engine reads "past" strictly - so the server sends
// days_ago_min and the floor is honored here, not just on the server.
const clampDays = (raw, action) => {
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) {
    return action.default_days_ago ?? 0;
  }
  const min = action.days_ago_min ?? 0;
  const max = action.days_ago_max ?? 3650;
  return Math.min(Math.max(parsed, min), max);
};

/**
 * One server-described action: a button, plus the days-ago offset and the
 * resulting-date preview when the action takes one. A disabled action stays
 * visible with its server-supplied reason as a tooltip - hiding it would leave
 * the tester guessing why nothing can be moved.
 *
 * Every action moves real CMS deadline rows, so the server also sends a tooltip
 * explaining what each button (and each days-ago box) actually changes. The
 * tooltip lives on the wrapper span while the action is disabled, because a
 * disabled button fires no hover events of its own.
 */
function ActionControl ({ action, days, onDaysChange, onRun, busy, testId }) {
  const disabled = busy || !action.enabled;
  const blocked = disabled && !!action.disabled_reason;
  return (
    <span
      className={styles.action}
      data-tooltip-id={blocked ? 'tooltip' : undefined}
      data-tooltip-content={blocked ? action.disabled_reason : undefined}
    >
      <button
        type="button"
        // The danger action reuses the shared red button so it looks like every
        // other destructive action in the portal.
        className={action.danger ? 'stop-button' : 'white'}
        disabled={disabled}
        onClick={() => onRun(action, clampDays(days, action))}
        data-testid={testId}
        data-tooltip-id={!blocked && action.tooltip ? 'tooltip' : undefined}
        data-tooltip-content={!blocked && action.tooltip ? action.tooltip : undefined}
      >
        {action.label}
      </button>
      {action.takes_days_ago && (
        <span
          className={styles.days}
          data-tooltip-id={!blocked ? 'tooltip' : undefined}
          data-tooltip-content={!blocked ? action.days_tooltip || Strings.accessObligationsDaysTooltip : undefined}
        >
          <TextInput
            id={`access-obligations-days-${testId}`}
            type="text"
            onlyDigits
            align="left"
            value={days}
            onChange={event => onDaysChange(event.target.value)}
            disabled={disabled}
            aria-label={`${action.label} ${Strings.accessObligationsDaysAgo}`}
          />
          <span className={styles.daysLabel}>{Strings.accessObligationsDaysAgo}</span>
          {action.preview && (
            <span
              className={styles.preview}
              data-tooltip-id={!blocked && action.preview_tooltip ? 'tooltip' : undefined}
              data-tooltip-content={!blocked && action.preview_tooltip ? action.preview_tooltip : undefined}
            >
              {action.preview}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

ActionControl.propTypes = {
  action: PropTypes.object.isRequired,
  days: PropTypes.string.isRequired,
  onDaysChange: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  testId: PropTypes.string.isRequired,
};

/**
 * Superuser tab for walking a test organization's CMS ACCESS deadline register
 * forward in time: every live obligation of every ACCESS enrollment, with
 * buttons that move its whole set of timestamps to now (or past its deadline) so
 * the deadline cron produces the next status without waiting for real days to
 * pass. Nothing here mocks the clock - the rows really move, which is exactly
 * what makes the tool usable against a deployed cloud environment.
 */
function AccessObligations({
  userId, list, move, recheck, prepareEndOfPeriod, triggerEarlySuccess, getMyOrgs,
  showNotification, openConfirmModal,
}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Days-ago offsets the tester typed, keyed per action instance; unset entries
  // fall back to the server-provided default.
  const [days, setDays] = useState({});
  // Only the latest list call may update the table: the org can be switched
  // while a load is in flight, and the older org's answer must not land last.
  const latestLoad = useRef(0);

  const showError = resp => showNotification(
    resp?.response?.data?.standard_error?.message || Strings.errors.internalError,
    5000,
    true,
  );

  // The org the tab drives; a superuser normally has none of his own, so it is
  // picked explicitly and sent with every call.
  const { orgs, orgUUID, setOrgUUID, orgsLoaded } = useAccessOrgSelection(userId, getMyOrgs, showError);

  const load = useCallback(targetOrgUUID => {
    latestLoad.current += 1;
    const loadId = latestLoad.current;
    setIsLoading(true);
    return list(targetOrgUUID).then(resp => {
      if (loadId !== latestLoad.current) return;
      if (resp.type === LIST_ACCESS_OBLIGATIONS_RESULT) {
        setData(resp.response);
      } else {
        setData(null);
        showError(resp);
      }
      setIsLoading(false);
    });
  }, [list]);

  // Wait for the org list before loading: without it a superuser with no org of
  // his own cannot be offered anything to select. And never hit the endpoint
  // without an org - the server rejects an org-less list with a 403, which the
  // global API middleware would surface as a red toast. An empty register is the
  // right state until the tester picks one.
  useEffect(() => {
    if (!orgsLoaded) return;
    if (!orgUUID) {
      setData(null);
      setIsLoading(false);
      return;
    }
    load(orgUUID);
  }, [orgsLoaded, orgUUID, load]);

  // run performs a mutating call, reports the outcome and reloads the table so
  // the timestamps shown are the ones the server just wrote.
  const run = useCallback((call, successMessage) => {
    setIsSaving(true);
    return call().then(resp => {
      if (!resp.type.endsWith('/result')) {
        showError(resp);
      } else if (successMessage) {
        showNotification(`${successMessage} ${resp.response?.cron_note || DEFAULT_CRON_NOTE}`, 8000);
      }
      return load(orgUUID);
    }).finally(() => setIsSaving(false));
  }, [load, orgUUID, showNotification]);

  const getDays = key => days[key];
  const setDayValue = (key, value) => setDays(prev => ({ ...prev, [key]: value }));

  // confirmOrRun shows the server's confirmation text for anything destructive
  // (every danger action carries one) and runs the action either way.
  const confirmOrRun = (action, invoke) => {
    if (action.confirm) {
      openConfirmModal({ text: action.confirm, onConfirm: invoke });
      return;
    }
    invoke();
  };

  const runObligationAction = (obligation, action, daysAgo) => {
    const invoke = () => (action.action === 'recheck'
      ? run(() => recheck(obligation.id, orgUUID), Strings.accessObligationsRechecked)
      : run(() => move(obligation.id, action.action, daysAgo, orgUUID), Strings.accessObligationsMoved));
    confirmOrRun(action, invoke);
  };

  const runEnrollmentAction = (enrollment, action, daysAgo) => {
    const invoke = () => run(
      () => (action.action === 'trigger_early_success'
        ? triggerEarlySuccess(enrollment.enrollment_uuid, daysAgo, orgUUID)
        : prepareEndOfPeriod(enrollment.enrollment_uuid, daysAgo, orgUUID)),
      Strings.accessObligationsPrepared,
    );
    confirmOrRun(action, invoke);
  };

  const renderRowActions = info => {
    const obligation = info.row.original;
    return (
      <div className={styles.rowActions}>
        {obligation.actions.map(action => {
          const key = `o${obligation.id}:${action.action}`;
          return (
            <ActionControl
              key={key}
              action={action}
              days={getDays(key) ?? String(action.default_days_ago ?? 0)}
              onDaysChange={value => setDayValue(key, value)}
              onRun={(act, daysAgo) => runObligationAction(obligation, act, daysAgo)}
              busy={isSaving}
              testId={`obligation-${action.action}-${obligation.id}`}
            />
          );
        })}
        <ActionControl
          key={`o${obligation.id}:recheck`}
          action={obligation.recheck_action}
          days=""
          onDaysChange={() => {}}
          onRun={() => runObligationAction(obligation, obligation.recheck_action, 0)}
          busy={isSaving}
          testId={`obligation-recheck-${obligation.id}`}
        />
      </div>
    );
  };

  const renderWarnings = info => {
    const warnings = info.getValue() || [];
    if (warnings.length === 0) {
      return '-';
    }
    return (
      <div className={styles.warnings}>
        {warnings.map(warning => (
          <div key={warning.code} className={styles.warning}>
            <div className="icon-and-text-container">
              <div className="icon-container warning" />
              <span>{warning.text}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    columnHelper.accessor('obligation_type', { header: Strings.type, size: 160 }),
    columnHelper.accessor('status', {
      header: Strings.status,
      size: 110,
      enableSorting: false,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('due_at', {
      header: Strings.accessObligationsDueAt, size: 150, enableSorting: false, cell: info => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('internal_due_at', {
      header: Strings.accessObligationsInternalDueAt, size: 150, enableSorting: false, cell: info => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('window', {
      header: Strings.accessObligationsWindow,
      size: 280,
      enableSorting: false,
      cell: info => fmtWindow(info.row.original),
    }),
    columnHelper.accessor('fire_at', {
      header: Strings.accessObligationsFireAt, size: 150, enableSorting: false, cell: info => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('next_recheck_at', {
      header: Strings.accessObligationsNextRecheck, size: 150, enableSorting: false, cell: info => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('warnings', {
      header: Strings.accessObligationsCaution, size: 300, enableSorting: false, cell: renderWarnings,
    }),
    columnHelper.accessor('actions', {
      header: Strings.actions, size: 560, enableSorting: false, cell: renderRowActions,
    }),
  ];

  const enrollments = data?.enrollments || [];
  const pointed = data?.pointed_at_simulator === true;

  return (
    <>
      <PageHeader
        left={Strings.accessObligationsTab}
        right={(
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            title={Strings.refresh}
            onClick={() => load(orgUUID)}
            disabled={!orgUUID || isLoading || isSaving}
            testid="refresh-access-obligations"
          />
        )}
      />

      <div className={styles.accessObligations}>
        <div className={styles.toolbar}>
          <span className={styles.fieldLabel}>{Strings.accessSimOrg}</span>
          <Select
            id="access-obligations-org"
            data={orgs}
            value={orgUUID}
            onChange={option => setOrgUUID(option?.value ?? null)}
            isSearchable
            disabled={isSaving}
          />
          <span className={styles.serverNow}>
            {Strings.accessObligationsServerNow}
            <span className={styles.serverNowValue}>{data ? fmtDate(data.server_now) : '-'}</span>
          </span>
        </div>

        {!orgUUID && orgsLoaded && (
          <p className={styles.notice}>{Strings.errors.emptyOrgId}</p>
        )}
        {orgUUID && !pointed && !isLoading && (
          <p className={styles.notice}>{Strings.accessObligationsNotPointedHint}</p>
        )}
        {pointed && (
          <p className={styles.notice}>{data?.cron_note || DEFAULT_CRON_NOTE}</p>
        )}

        {!isLoading && pointed && enrollments.length === 0 && (
          <p className={styles.notice}>{Strings.accessObligationsNoEnrollments}</p>
        )}

        {enrollments.map(enrollment => (
          <section key={enrollment.enrollment_uuid} className={styles.enrollment}>
            <div className={styles.enrollmentHeader}>
              <h2>
                {enrollment.patient_name || Strings.patient}
                <span className={styles.patientId}>{enrollment.patient_masked_id}</span>
                <span
                  data-tooltip-id="tooltip"
                  data-tooltip-content={Strings.accessSimTrackTooltip}
                  className={styles.track}
                >
                  {enrollment.track || '-'}
                </span>
                {enrollment.terminated && (
                  <span className={styles.terminated}>{Strings.accessObligationsTerminated}</span>
                )}
              </h2>
              <div className={styles.enrollmentActions}>
                {enrollment.enrollment_actions.map(action => {
                  const key = `e${enrollment.enrollment_uuid}:${action.action}`;
                  return (
                    <ActionControl
                      key={key}
                      action={action}
                      days={getDays(key) ?? String(action.default_days_ago ?? 0)}
                      onDaysChange={value => setDayValue(key, value)}
                      onRun={(act, daysAgo) => runEnrollmentAction(enrollment, act, daysAgo)}
                      busy={isSaving}
                      testId={`enrollment-${action.action}-${enrollment.enrollment_uuid}`}
                    />
                  );
                })}
              </div>
            </div>
            <Table3
              name={`access-obligations-${enrollment.enrollment_uuid}`}
              data={enrollment.obligations}
              columns={columns}
              isLoading={isLoading}
              noPagination
            />
          </section>
        ))}
      </div>
    </>
  );
}

AccessObligations.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined, null])]),
  list: PropTypes.func.isRequired,
  move: PropTypes.func.isRequired,
  recheck: PropTypes.func.isRequired,
  prepareEndOfPeriod: PropTypes.func.isRequired,
  triggerEarlySuccess: PropTypes.func.isRequired,
  getMyOrgs: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  list: orgUUID => dispatch(actions.list(orgUUID)),
  move: (obligationID, action, daysAgo, orgUUID) => dispatch(actions.move(obligationID, action, daysAgo, orgUUID)),
  recheck: (obligationID, orgUUID) => dispatch(actions.recheck(obligationID, orgUUID)),
  prepareEndOfPeriod: (enrollmentUUID, daysAgo, orgUUID) => dispatch(actions.prepareEndOfPeriod(enrollmentUUID, daysAgo, orgUUID)),
  triggerEarlySuccess: (enrollmentUUID, daysAgo, orgUUID) => dispatch(actions.triggerEarlySuccess(enrollmentUUID, daysAgo, orgUUID)),
  getMyOrgs: () => dispatch(actions.getMyOrgs()),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  openConfirmModal: data => dispatch(openModalAction(NewConfirmationModal.MODAL_NAME, data)),
});

const mapStateToProps = state => ({
  userId: state.auth?.profile?.id,
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessObligations);
